import React, { useContext, useEffect, useRef, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import "../../../assets/css/gcadmin/admindashboardpage/AdminDashboardPage.css";

// ── Dispute status badge colour map ───────────────────────────────────────────
const DISPUTE_STATUS_CLASS = {
  open:       "gcAdmin-dash__ticket-badge--open",
  pending:    "gcAdmin-dash__ticket-badge--open",
  resolved:   "gcAdmin-dash__ticket-badge--resolved",
  approved:   "gcAdmin-dash__ticket-badge--resolved",
  "in review": "gcAdmin-dash__ticket-badge--inreview",
  rejected:   "gcAdmin-dash__ticket-badge--rejected",
};

// ── Stat card icon definitions (same SVGs as before) ─────────────────────────
const STAT_ICONS = {
  totalBooks: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M4 19.5A2.5 2.5 0 016.5 17H20" stroke="#177a8a" strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" stroke="#177a8a" strokeWidth="1.8"/>
    </svg>
  ),
  activeUsers: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="#177a8a" strokeWidth="1.8" strokeLinecap="round"/>
      <circle cx="9" cy="7" r="4" stroke="#177a8a" strokeWidth="1.8"/>
      <path d="M23 21v-2a4 4 0 00-3-3.87" stroke="#177a8a" strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M16 3.13a4 4 0 010 7.75" stroke="#177a8a" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  ),
  pendingReservations: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="4" width="18" height="18" rx="2" stroke="#e0940a" strokeWidth="1.8"/>
      <path d="M3 9h18" stroke="#e0940a" strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M8 2v4M16 2v4" stroke="#e0940a" strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M8 14h5M8 17h3" stroke="#e0940a" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  ),
  overdueBooks: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#e03e3e" strokeWidth="1.8" strokeLinejoin="round"/>
      <path d="M12 9v4M12 17h.01" stroke="#e03e3e" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  ),
  notesUploaded: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#177a8a" strokeWidth="1.8" strokeLinejoin="round"/>
      <path d="M14 2v6h6M9 13h6M9 17h4" stroke="#177a8a" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  ),
  booksIssuedToday: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M3 17l4-4 4 4 4-6 4 3" stroke="#15a06e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M21 7l-2-2-2 2" stroke="#15a06e" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  ),
};

const STAT_ICON_BG = {
  totalBooks:          "#dce8f0",
  activeUsers:         "#dce8f0",
  pendingReservations: "#fef3e0",
  overdueBooks:        "#fdecea",
  notesUploaded:       "#dce8f0",
  booksIssuedToday:    "#e0f5ee",
};

const STAT_LABELS = {
  totalBooks:          "Total Books",
  activeUsers:         "Active Users",
  pendingReservations: "Pending Reservations",
  overdueBooks:        "Overdue Books",
  notesUploaded:       "Notes Uploaded",
  booksIssuedToday:    "Books Issued Today",
};

// ── Role icon (student = grad cap, teacher = person) ─────────────────────────
const RoleIcon = ({ role }) => (
  <div className={`gcAdmin-dash__role-icon gcAdmin-dash__role-icon--${role?.toLowerCase()}`}>
    {role?.toLowerCase() === "teacher" ? (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.8"/>
        <path d="M4 21c0-3.87 3.58-7 8-7s8 3.13 8 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ) : (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
        <path d="M2 9l10-5 10 5-10 5-10-5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
        <path d="M6 11v5c0 1.5 3 3 6 3s6-1.5 6-3v-5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      </svg>
    )}
  </div>
);

// ── Star rating ────────────────────────────────────────────────────────────
const StarRating = ({ rating }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    const filled = i <= Math.round(rating || 0);
    stars.push(
      <svg key={i} width="13" height="13" viewBox="0 0 24 24" fill={filled ? "#e0940a" : "none"} stroke="#e0940a" strokeWidth="1.4">
        <polygon points="12 2 15 9 22 9 16.5 13.5 18.5 21 12 17 5.5 21 7.5 13.5 2 9 9 9" />
      </svg>
    );
  }
  return <div className="gcAdmin-dash__stars">{stars}</div>;
};

// ── Animated counter ──────────────────────────────────────────────────────────
const Counter = ({ target, visible }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!visible) return;
    const num = typeof target === "string"
      ? parseInt(target.replace(/,/g, ""), 10)
      : target;
    if (!num) return;
    let n = 0;
    const step = Math.ceil(num / 55) || 1;
    const t = setInterval(() => {
      n += step;
      if (n >= num) { setVal(num); clearInterval(t); }
      else setVal(n);
    }, 16);
    return () => clearInterval(t);
  }, [visible, target]);
  return <>{val.toLocaleString()}</>;
};

// ── Format date ───────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
};

// ── Stat card ─────────────────────────────────────────────────────────────────
const StatCard = ({ statKey, value, index, visible, loading }) => (
  <Col xs={12} sm={6} lg={4}>
    <div
      className={`gcAdmin-dash-stat ${visible ? "gcAdmin-dash-stat--in" : ""}`}
      style={{ transitionDelay: `${index * 0.08}s` }}
    >
      <div className="gcAdmin-dash-stat__info">
        <p className="gcAdmin-dash-stat__label">{STAT_LABELS[statKey]}</p>
        <h3 className="gcAdmin-dash-stat__value">
          {loading ? "—" : <Counter target={value} visible={visible} />}
        </h3>
      </div>
      <div className="gcAdmin-dash-stat__icon" style={{ background: STAT_ICON_BG[statKey] }}>
        {STAT_ICONS[statKey]}
      </div>
    </div>
  </Col>
);

// ── Main Dashboard ────────────────────────────────────────────────────────────
const AdminDashboardPage = () => {
  const { baseUrl, authToken } = useContext(AppContext);

  const [pageIn,  setPageIn]  = useState(false);
  const [statsIn, setStatsIn] = useState(false);
  const [alertIn, setAlertIn] = useState(false);
  const [cardsIn, setCardsIn] = useState(false);

  const statsRef = useRef(null);
  const alertRef = useRef(null);
  const cardsRef = useRef(null);

  // ── Dynamic data states ──
  const [stats, setStats] = useState({
    totalBooks: 0, activeUsers: 0, pendingReservations: 0,
    overdueBooks: 0, notesUploaded: 0, booksIssuedToday: 0,
  });
  const [disputes, setDisputes]               = useState([]);
  const [overdue, setOverdue]                 = useState([]);
  const [reviews, setReviews]                 = useState([]);
  const [requests, setRequests]               = useState([]);

  const [statsLoading, setStatsLoading]       = useState(true);
  const [disputesLoading, setDisputesLoading] = useState(true);
  const [overdueLoading, setOverdueLoading]   = useState(true);
  const [reviewsLoading, setReviewsLoading]   = useState(true);
  const [requestsLoading, setRequestsLoading] = useState(true);

  useEffect(() => { setTimeout(() => setPageIn(true), 50); }, []);

  useEffect(() => {
    const mkObs = (setter) => new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setter(true); }, { threshold: 0.1 }
    );
    const s = mkObs(setStatsIn); if (statsRef.current) s.observe(statsRef.current);
    const a = mkObs(setAlertIn); if (alertRef.current) a.observe(alertRef.current);
    const c = mkObs(setCardsIn); if (cardsRef.current) c.observe(cardsRef.current);
    return () => { s.disconnect(); a.disconnect(); c.disconnect(); };
  }, []);

  // ── Fetch stats ──
  useEffect(() => {
    const formData = new FormData();

    axios
      .post(`${baseUrl}/admin/dashboard/fetchAdminStatsForDashboard`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data"
        },
      })
      .then((res) => {
        if (res.data.success) {
          setStats({
            totalBooks:          res.data.totalBooks          ?? 0,
            activeUsers:         res.data.activeUsers         ?? 0,
            pendingReservations: res.data.pendingReservations ?? 0,
            overdueBooks:        res.data.overdueBooks        ?? 0,
            notesUploaded:       res.data.notesUploaded       ?? 0,
            booksIssuedToday:    res.data.booksIssuedToday    ?? 0,
          });
        }
      })
      .catch((err) => console.error("Failed to fetch admin stats:", err))
      .finally(() => setStatsLoading(false));
  }, [baseUrl, authToken]);

  // ── Fetch recent disputes ──
  useEffect(() => {
    const formData = new FormData();

    axios
      .post(`${baseUrl}/admin/dashboard/fetchAdminRecentDisputes`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data"
        },
      })
      .then((res) => {
        if (res.data.success) {
          setDisputes(res.data.disputes ?? []);
        }
      })
      .catch((err) => console.error("Failed to fetch admin disputes:", err))
      .finally(() => setDisputesLoading(false));
  }, [baseUrl, authToken]);

  // ── Fetch overdue book alerts ──
  useEffect(() => {
    const formData = new FormData();

    axios
      .post(`${baseUrl}/admin/dashboard/fetchAdminOverdueAlerts`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data"
        },
      })
      .then((res) => {
        if (res.data.success) {
          setOverdue(res.data.overdue ?? []);
        }
      })
      .catch((err) => console.error("Failed to fetch admin overdue alerts:", err))
      .finally(() => setOverdueLoading(false));
  }, [baseUrl, authToken]);

  // ── Fetch recent reviews ──
  useEffect(() => {
    const formData = new FormData();

    axios
      .post(`${baseUrl}/admin/dashboard/fetchAdminRecentReviews`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data"
        },
      })
      .then((res) => {
        if (res.data.success) {
          setReviews(res.data.reviews ?? []);
        }
      })
      .catch((err) => console.error("Failed to fetch admin reviews:", err))
      .finally(() => setReviewsLoading(false));
  }, [baseUrl, authToken]);

  // ── Fetch recent requests ──
  useEffect(() => {
    const formData = new FormData();

    axios
      .post(`${baseUrl}/admin/dashboard/fetchAdminRecentRequests`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data"
        },
      })
      .then((res) => {
        if (res.data.success) {
          setRequests(res.data.requests ?? []);
        }
      })
      .catch((err) => console.error("Failed to fetch admin requests:", err))
      .finally(() => setRequestsLoading(false));
  }, [baseUrl, authToken]);

  const STAT_KEYS = [
    "totalBooks", "activeUsers", "pendingReservations",
    "overdueBooks", "notesUploaded", "booksIssuedToday",
  ];

  return (
    <div className={`gcAdmin-dash ${pageIn ? "gcAdmin-dash--in" : ""}`}>
      <Container fluid className="gcAdmin-dash__container">

        {/* Title */}
        <h2 className="gcAdmin-dash__title">Admin Dashboard</h2>

        {/* ── Stat Cards (Dynamic) ── */}
        <Row className="gy-3 gx-3 mb-4" ref={statsRef}>
          {STAT_KEYS.map((key, i) => (
            <StatCard
              key={key}
              statKey={key}
              value={stats[key]}
              index={i}
              visible={statsIn}
              loading={statsLoading}
            />
          ))}
        </Row>

        {/* ── Overdue Alerts (Dynamic) ── */}
        <div
          ref={alertRef}
          className={`gcAdmin-dash__alerts ${alertIn ? "gcAdmin-dash__alerts--in" : ""}`}
        >
          <div className="gcAdmin-dash__alerts-header">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#e03e3e" strokeWidth="1.8" strokeLinejoin="round"/>
              <path d="M12 9v4M12 17h.01" stroke="#e03e3e" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            <span className="gcAdmin-dash__alerts-title">Overdue Book Alerts</span>
          </div>

          {overdueLoading ? (
            <p className="gcAdmin-dash__card-sub" style={{ padding: "12px 0" }}>
              Loading overdue alerts...
            </p>
          ) : overdue.length === 0 ? (
            <p className="gcAdmin-dash__card-sub" style={{ padding: "12px 0" }}>
              No overdue books.
            </p>
          ) : (
            overdue.map((item, i) => (
              <div
                key={item.id}
                className={`gcAdmin-dash__alert-row ${alertIn ? "gcAdmin-dash__alert-row--in" : ""}`}
                style={{ transitionDelay: `${0.1 + i * 0.1}s` }}
              >
                <div className="gcAdmin-dash__alert-left">
                  <div className="gcAdmin-dash__alert-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="#e03e3e" strokeWidth="1.8"/>
                      <path d="M12 8v4M12 16h.01" stroke="#e03e3e" strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div>
                    <p className="gcAdmin-dash__alert-name">
                      {item.name} {item.roll_no ? `(${item.roll_no})` : ""}
                    </p>
                    <p className="gcAdmin-dash__alert-book">
                      {item.book} • Due: {formatDate(item.due_date)}
                    </p>
                  </div>
                </div>
                <div className="gcAdmin-dash__alert-right">
                  <span className="gcAdmin-dash__alert-days">{item.days} days overdue</span>
                  <span className="gcAdmin-dash__alert-fine">Fine: Rs. {item.fine}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* ── Bottom 3 Cards ── */}
        <Row
          ref={cardsRef}
          className={`gy-3 gx-3 ${cardsIn ? "gcAdmin-dash__bottom--in" : ""}`}
        >

          {/* ── Recent Reviews (Dynamic) ── */}
          <Col xs={12} lg={4}>
            <div className={`gcAdmin-dash__card ${cardsIn ? "gcAdmin-dash__card--in" : ""}`} style={{ transitionDelay: "0.05s" }}>
              <div className="gcAdmin-dash__card-header">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <polygon points="12 2 15 9 22 9 16.5 13.5 18.5 21 12 17 5.5 21 7.5 13.5 2 9 9 9" stroke="#e0940a" strokeWidth="1.5" strokeLinejoin="round"/>
                </svg>
                <span className="gcAdmin-dash__card-title">Recent Reviews</span>
                <a href="#!" className="gcAdmin-dash__card-viewall">View All →</a>
              </div>

              {reviewsLoading ? (
                <p className="gcAdmin-dash__card-sub" style={{ padding: "12px 0" }}>
                  Loading reviews...
                </p>
              ) : reviews.length === 0 ? (
                <p className="gcAdmin-dash__card-sub" style={{ padding: "12px 0" }}>
                  No recent reviews found.
                </p>
              ) : (
                reviews.map((r) => (
                  <div key={r.id} className="gcAdmin-dash__card-row gcAdmin-dash__review-row">
                    <div className="gcAdmin-dash__review-body">
                      <div className="gcAdmin-dash__review-head">
                        <span className="gcAdmin-dash__card-name">{r.name}</span>
                        <span className="gcAdmin-dash__review-role">• {r.role}</span>
                      </div>
                      <StarRating rating={r.rating} />
                      <p className="gcAdmin-dash__card-sub">{r.review}</p>
                      <span className="gcAdmin-dash__card-date">{formatDate(r.created_at)}</span>
                    </div>
                    <span
                      className={`gcAdmin-dash__ticket-badge ${
                        DISPUTE_STATUS_CLASS[r.status?.toLowerCase()] ?? "gcAdmin-dash__ticket-badge--open"
                      }`}
                    >
                      {r.status}
                    </span>
                  </div>
                ))
              )}
            </div>
          </Col>

          {/* ── Recent Disputes (Dynamic) ── */}
          <Col xs={12} lg={4}>
            <div className={`gcAdmin-dash__card ${cardsIn ? "gcAdmin-dash__card--in" : ""}`} style={{ transitionDelay: "0.12s" }}>
              <div className="gcAdmin-dash__card-header">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#e0940a" strokeWidth="1.8"/>
                  <path d="M12 8v4M12 16h.01" stroke="#e0940a" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
                <span className="gcAdmin-dash__card-title">Recent Disputes</span>
                <a href="#!" className="gcAdmin-dash__card-viewall">View All →</a>
              </div>

              {disputesLoading ? (
                <p className="gcAdmin-dash__card-sub" style={{ padding: "12px 0" }}>
                  Loading disputes...
                </p>
              ) : disputes.length === 0 ? (
                <p className="gcAdmin-dash__card-sub" style={{ padding: "12px 0" }}>
                  No recent disputes found.
                </p>
              ) : (
                disputes.map((d) => (
                  <div key={d.id} className="gcAdmin-dash__card-row gcAdmin-dash__dispute-row">
                    <div className="gcAdmin-dash__dispute-left">
                      <RoleIcon role={d.role} />
                      <div>
                        <div className="gcAdmin-dash__dispute-head">
                          <span className="gcAdmin-dash__card-name">{d.name}</span>
                          <span className={`gcAdmin-dash__role-badge gcAdmin-dash__role-badge--${d.role?.toLowerCase()}`}>
                            {d.role}
                          </span>
                          <span className="gcAdmin-dash__ticket-no">{d.ticket}</span>
                        </div>
                        <p className="gcAdmin-dash__card-sub">{d.subject}</p>
                      </div>
                    </div>
                    <div className="gcAdmin-dash__dispute-right">
                      <span
                        className={`gcAdmin-dash__ticket-badge ${
                          DISPUTE_STATUS_CLASS[d.status?.toLowerCase()] ?? "gcAdmin-dash__ticket-badge--open"
                        }`}
                      >
                        {d.status}
                      </span>
                      <span className="gcAdmin-dash__card-date">{formatDate(d.created_at)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Col>

          {/* ── Recent Requests (Dynamic) ── */}
          <Col xs={12} lg={4}>
            <div className={`gcAdmin-dash__card ${cardsIn ? "gcAdmin-dash__card--in" : ""}`} style={{ transitionDelay: "0.2s" }}>
              <div className="gcAdmin-dash__card-header">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="#177a8a" strokeWidth="1.8" strokeLinejoin="round"/>
                  <polyline points="14 2 14 8 20 8" stroke="#177a8a" strokeWidth="1.8"/>
                </svg>
                <span className="gcAdmin-dash__card-title">Recent Requests</span>
                <a href="#!" className="gcAdmin-dash__card-viewall">View All →</a>
              </div>

              {requestsLoading ? (
                <p className="gcAdmin-dash__card-sub" style={{ padding: "12px 0" }}>
                  Loading requests...
                </p>
              ) : requests.length === 0 ? (
                <p className="gcAdmin-dash__card-sub" style={{ padding: "12px 0" }}>
                  No recent requests found.
                </p>
              ) : (
                requests.map((r) => (
                  <div key={r.id} className="gcAdmin-dash__card-row">
                    <div>
                      <p className="gcAdmin-dash__card-name">{r.name}</p>
                      <p className="gcAdmin-dash__card-sub">{r.book}</p>
                    </div>
                    <div className="gcAdmin-dash__req-right">
                      <span className={`gcAdmin-dash__req-badge gcAdmin-dash__req-badge--${r.type.toLowerCase()}`}>{r.type}</span>
                      <span className="gcAdmin-dash__card-date">{formatDate(r.created_at)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Col>

        </Row>
      </Container>
    </div>
  );
};

export default AdminDashboardPage;